import { useEffect, useMemo, useState } from 'react';
import { FaPlus, FaSearch, FaEdit, FaCheckCircle, FaRegTimesCircle } from 'react-icons/fa';
import EmpresaNuevoModal from './EmpresaNuevoModal';
import EmpresaSoftwareCrud from './EmpresaSoftwareCrud';
import EmpresaNominaSoftwareCrud from './EmpresaNominaSoftwareCrud';
import EmpresaCertificadoCrud from './EmpresaCertificadoCrud';
import EmpresaPOSSoftware from './EmpresaPOSSoftware';
import LoadingComponent from './../../widgets/LoadingComponent';
import { getData } from './../../api/ApiRest';
import { Link, useNavigate } from 'react-router-dom';
import { loadGeneralCompanies } from '../../components/services/companyAPI';
import {
    Box, Paper, Grid, TextField, MenuItem, Button, Typography,
    Breadcrumbs, Chip, Tooltip, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// ── Estilos de tabla ──────────────────────────────────────────────────────────

const HeaderCell = styled(TableCell)(() => ({
    backgroundColor: '#224B6A',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
    padding: '10px 12px',
}));

const BodyCell = styled(TableCell)(() => ({
    fontSize: '0.78rem',
    padding: '7px 12px',
    borderColor: '#dee2e6',
}));

// ── Helpers ───────────────────────────────────────────────────────────────────

const AmbienteChip = ({ ambienteid }) =>
    ambienteid === 1
        ? <Chip label="Producción" size="small"
            sx={{ bgcolor: '#e0f0fb', color: '#224B6A', fontWeight: 600, fontSize: '0.68rem', cursor: 'pointer' }} />
        : <Chip label="Prueba" size="small"
            sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontSize: '0.68rem', cursor: 'pointer' }} />;

const inputSx = {
    '& .MuiInputBase-input': { fontSize: '0.82rem' },
    '& .MuiInputLabel-root': { fontSize: '0.82rem' },
};

// ── Componente principal ──────────────────────────────────────────────────────

function Empresa() {
    const navigate = useNavigate();

    const [modalNuevo, setModalNuevo] = useState(false);
    const [softwareModal, setSoftwareModal] = useState(false);
    const [nominaSoftwareModal, setNominaSoftwareModal] = useState(false);
    const [posSoftwareModal, setPOSSoftwareModal] = useState(false);
    const [certificateModal, setCertificateModal] = useState(false);
    const [filterCompanyValue, setFilterCompanyValue] = useState('all');
    const [companies, setCompanies] = useState([]);
    const [companySelect, setCompanySelect] = useState({});
    const [loading, setLoading] = useState(false);
    const [dataBuscar, setDataBuscar] = useState({
        Nit: '', Nombre: '', FE_Etapa: '0', Nomina_Etapa: '0', DocEquiv_Etapa: '0'
    });

    const resultFilterCompanies = useMemo(() => {
        return companies?.filter(comp => {
            if (filterCompanyValue === 'userDisabled') return comp?.activeCompany === false;
            if (filterCompanyValue === 'all') return true;
            return comp?.activeCompany;
        });
    }, [companies, filterCompanyValue]);

    // ── Handlers de modales ───────────────────────────────────────────────────

    const softwareModalCerrar = (recargar) => {
        setSoftwareModal(false);
        if (recargar === true) buscarCompanies({ preventDefault: () => {} });
    };

    const nominaSoftwareModalCerrar = (recargar) => {
        setNominaSoftwareModal(false);
        if (recargar === true) buscarCompanies({ preventDefault: () => {} });
    };

    const certificateModalCerrar = (recargar) => {
        setCertificateModal(false);
        if (recargar === true) buscarCompanies({ preventDefault: () => {} });
    };

    const cerrarModalNuevo = () => {
        setModalNuevo(false);
        setCompanySelect({});
    };

    // ── API ───────────────────────────────────────────────────────────────────

    const buscarCompanies = (event) => {
        event.preventDefault();
        setCompanies([]);
        setLoading(true);
        getData('api/Configuracion/Company/IndexGetData',
            { Nit: dataBuscar.Nit, Descripcion: dataBuscar.Nombre }, false)
            .then(response => {
                if (response.status === true) setCompanies(response.companies);
                else alert(response.msj);
                setLoading(false);
            }, error => {
                console.log(error);
                alert('Algo salió mal en el cargue inicial');
                setLoading(false);
            });
    };

    const loadCompanies = async () => {
        setLoading(true);
        const result = await loadGeneralCompanies(dataBuscar);
        setCompanies(result);
        setLoading(false);
    };

    useEffect(() => { loadCompanies(); }, []);

    const set = (field) => (e) => setDataBuscar(prev => ({ ...prev, [field]: e.target.value }));

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Box sx={{ p: 2 }}>
            {/* Encabezado */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs sx={{ fontSize: '0.82rem' }}>
                    <Typography color="inherit" fontSize="0.82rem">Empresas</Typography>
                    <Typography color="text.primary" fontSize="0.82rem">Empresas</Typography>
                </Breadcrumbs>
                <Button variant="contained" size="small" startIcon={<FaPlus size={11} />}
                    onClick={() => setModalNuevo(true)}
                    sx={{ background: '#224B6A', '&:hover': { background: '#1a3a54' }, fontSize: '0.78rem' }}>
                    Nueva Empresa
                </Button>
            </Box>

            {/* Filtros */}
            <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}
                component="form" onSubmit={buscarCompanies}>
                <Grid container spacing={1.5} alignItems="flex-end">

                    <Grid item xs={6} sm={3} md={2}>
                        <TextField label="NIT" type="number" fullWidth size="small"
                            autoComplete="off" value={dataBuscar.Nit} onChange={set('Nit')} sx={inputSx} />
                    </Grid>

                    <Grid item xs={6} sm={3} md={3}>
                        <TextField label="Nombre" fullWidth size="small"
                            autoComplete="off" value={dataBuscar.Nombre} onChange={set('Nombre')} sx={inputSx} />
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                        <TextField label="FE Etapa" select fullWidth size="small"
                            value={dataBuscar.FE_Etapa} onChange={set('FE_Etapa')} sx={inputSx}>
                            <MenuItem value="0" sx={{ fontSize: '0.82rem' }}>TODOS</MenuItem>
                            <MenuItem value="1" sx={{ fontSize: '0.82rem' }}>PRUEBA</MenuItem>
                            <MenuItem value="2" sx={{ fontSize: '0.82rem' }}>PRODUCCIÓN</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                        <TextField label="Nómina Etapa" select fullWidth size="small"
                            value={dataBuscar.Nomina_Etapa} onChange={set('Nomina_Etapa')} sx={inputSx}>
                            <MenuItem value="0" sx={{ fontSize: '0.82rem' }}>TODOS</MenuItem>
                            <MenuItem value="1" sx={{ fontSize: '0.82rem' }}>PRUEBA</MenuItem>
                            <MenuItem value="2" sx={{ fontSize: '0.82rem' }}>PRODUCCIÓN</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                        <TextField label="Doc. Equiv. Etapa" select fullWidth size="small"
                            value={dataBuscar.DocEquiv_Etapa} onChange={set('DocEquiv_Etapa')} sx={inputSx}>
                            <MenuItem value="0" sx={{ fontSize: '0.82rem' }}>TODOS</MenuItem>
                            <MenuItem value="1" sx={{ fontSize: '0.82rem' }}>PRUEBA</MenuItem>
                            <MenuItem value="2" sx={{ fontSize: '0.82rem' }}>PRODUCCIÓN</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={6} sm={3} md={2}>
                        <TextField label="Estado" select fullWidth size="small"
                            value={filterCompanyValue}
                            onChange={e => setFilterCompanyValue(e.target.value)} sx={inputSx}>
                            <MenuItem value="userEnabled" sx={{ fontSize: '0.82rem' }}>ACTIVOS</MenuItem>
                            <MenuItem value="userDisabled" sx={{ fontSize: '0.82rem' }}>INACTIVOS</MenuItem>
                            <MenuItem value="all" sx={{ fontSize: '0.82rem' }}>TODOS</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <Button type="submit" variant="contained" size="small"
                            startIcon={<FaSearch size={12} />}
                            sx={{ background: '#224B6A', '&:hover': { background: '#1a3a54' }, fontSize: '0.78rem' }}>
                            Buscar
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <LoadingComponent Loading={loading} />

            {/* Contador */}
            <Typography variant="body2" fontWeight={700} color="#224B6A" sx={{ mb: 1 }}>
                Total: {resultFilterCompanies.length} compañía{resultFilterCompanies.length !== 1 ? 's' : ''}
            </Typography>

            {/* Tabla */}
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <HeaderCell>#</HeaderCell>
                            <HeaderCell align="center">Acción</HeaderCell>
                            <HeaderCell>NIT</HeaderCell>
                            <HeaderCell>Nombre</HeaderCell>
                            <HeaderCell>Nombre Alterno</HeaderCell>
                            <HeaderCell>Usuario</HeaderCell>
                            <HeaderCell align="center">Certificado</HeaderCell>
                            <HeaderCell align="center">FE</HeaderCell>
                            <HeaderCell align="center">Nómina</HeaderCell>
                            <HeaderCell align="center">POS</HeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {resultFilterCompanies.length === 0 ? (
                            <TableRow>
                                <BodyCell colSpan={10} align="center" sx={{ color: '#9ca3af', py: 4 }}>
                                    Sin resultados.
                                </BodyCell>
                            </TableRow>
                        ) : (
                            resultFilterCompanies.map((item, key) => (
                                <TableRow key={item.nit}
                                    sx={{
                                        backgroundColor: !item.activeCompany ? '#f0f0f0' : key % 2 === 0 ? '#fff' : '#f8fafc',
                                        '&:hover': { backgroundColor: 'rgba(144, 203, 216, 0.2)' },
                                        opacity: !item.activeCompany ? 0.75 : 1,
                                    }}>
                                    <BodyCell>{key + 1}</BodyCell>
                                    <BodyCell align="center">
                                        <Tooltip title="Editar empresa">
                                            <IconButton size="small"
                                                component={Link}
                                                to={'/Home/Empresa/Config/' + item.id}
                                                target="_blank"
                                                sx={{ color: '#1976d2' }}>
                                                <FaEdit size={14} />
                                            </IconButton>
                                        </Tooltip>
                                    </BodyCell>
                                    <BodyCell sx={{ fontWeight: 500 }}>
                                        {item.nit}-{item.dv}
                                    </BodyCell>
                                    <BodyCell>{item.empresa_Nombre}</BodyCell>
                                    <BodyCell>{item.nombre_Alterno}</BodyCell>
                                    <BodyCell>{item.user_Email}</BodyCell>
                                    <BodyCell align="center"
                                        onClick={() => navigate('/Home/Empresa/Config/' + item.id)}
                                        sx={{ cursor: 'pointer' }}>
                                        {item.certificates === 0
                                            ? <Tooltip title="Sin certificado">
                                                <span><FaRegTimesCircle size={15} color="#dc2626" /></span>
                                              </Tooltip>
                                            : <Tooltip title="Certificado configurado">
                                                <span><FaCheckCircle size={15} color="#16a34a" /></span>
                                              </Tooltip>
                                        }
                                    </BodyCell>
                                    <BodyCell align="center"
                                        onClick={() => navigate('/Home/Empresa/Config/' + item.id)}
                                        sx={{ cursor: 'pointer' }}>
                                        <AmbienteChip ambienteid={item.facturaElectronica_Ambienteid} />
                                    </BodyCell>
                                    <BodyCell align="center"
                                        onClick={() => navigate('/Home/Empresa/Config/' + item.id)}
                                        sx={{ cursor: 'pointer' }}>
                                        <AmbienteChip ambienteid={item.nomina_Ambienteid} />
                                    </BodyCell>
                                    <BodyCell align="center"
                                        onClick={() => navigate('/Home/Empresa/Config/' + item.id)}
                                        sx={{ cursor: 'pointer' }}>
                                        <AmbienteChip ambienteid={item.docEquiv_Ambienteid} />
                                    </BodyCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Modales */}
            <EmpresaNuevoModal
                Show={modalNuevo}
                cerrarModal={cerrarModalNuevo}
                CompanySelect={companySelect || { id: null }}
            />
            <EmpresaSoftwareCrud
                Show={softwareModal}
                cerrarModal={softwareModalCerrar}
                CompanySelect={companySelect}
            />
            <EmpresaNominaSoftwareCrud
                Show={nominaSoftwareModal}
                cerrarModal={nominaSoftwareModalCerrar}
                CompanySelect={companySelect}
            />
            <EmpresaPOSSoftware
                Show={posSoftwareModal}
                cerrarModal={() => setPOSSoftwareModal(false)}
                CompanySelect={companySelect}
            />
            <EmpresaCertificadoCrud
                Show={certificateModal}
                cerrarModal={certificateModalCerrar}
                CompanySelect={companySelect}
                Nuevo={false}
            />
        </Box>
    );
}

export default Empresa;
